import { useCart } from "@/context/CartContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { optimizeImage } from "@/lib/images";

const CartSheet = () => {
  const { items, totals, updateQty, removeItem, open, setOpen } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setOpen(false);
    navigate("/commande");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col" data-testid="cart-sheet">
        <SheetHeader>
          <SheetTitle className="font-display  text-2xl">
            Votre panier
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <ShoppingBag className="h-12 w-12 text-[#a8b4bf] mb-4" />
            <p className="text-[#5b6b7a]">Votre panier est vide.</p>
            <Link
              to="/cartons-demenagement"
              onClick={() => setOpen(false)}
              className="mt-6 btn-primary"
              data-testid="empty-cart-cta"
            >
              Découvrir la boutique
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4 -mx-6 px-6 space-y-4" data-testid="cart-items-list">
              {items.map((item) => (
                <div
                  key={item.product_id}
                  className="flex gap-3 border-b border-[#eef2f6] pb-4"
                  data-testid={`cart-item-${item.slug}`}
                >
                  <img
                    src={optimizeImage(item.image, 200)}
                    alt={item.name}
                    loading="lazy"
                    decoding="async"
                    className="h-20 w-20 object-cover bg-[#F3EFE9]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <h4 className="text-sm font-semibold leading-tight">{item.name}</h4>
                      <button
                        onClick={() => removeItem(item.product_id)}
                        className="text-[#7a8a9a] hover:text-red-600"
                        data-testid={`cart-remove-${item.slug}`}
                        aria-label="Retirer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="text-xs text-[#5b6b7a] mt-1">
                      {item.price.toFixed(2).replace(".", ",")} € / unité
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center border border-[#e6edf3]">
                        <button
                          className="h-8 w-8 inline-flex items-center justify-center hover:bg-zinc-100"
                          onClick={() => updateQty(item.product_id, item.quantity - 1)}
                          data-testid={`cart-decrease-${item.slug}`}
                          aria-label="Diminuer"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="px-3 text-sm font-semibold" data-testid={`cart-qty-${item.slug}`}>
                          {item.quantity}
                        </span>
                        <button
                          className="h-8 w-8 inline-flex items-center justify-center hover:bg-zinc-100"
                          onClick={() => updateQty(item.product_id, item.quantity + 1)}
                          data-testid={`cart-increase-${item.slug}`}
                          aria-label="Augmenter"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="font-semibold text-sm">
                        {(item.price * item.quantity).toFixed(2).replace(".", ",")} €
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <SheetFooter className="border-t border-[#e6edf3] pt-4 mt-2 flex-col gap-3 sm:flex-col sm:space-x-0">
              <div className="w-full flex justify-between text-sm">
                <span className="text-[#5b6b7a]">Sous-total</span>
                <span className="font-semibold" data-testid="cart-subtotal">
                  {totals.subtotal.toFixed(2).replace(".", ",")} €
                </span>
              </div>
              <div className="w-full flex justify-between text-sm">
                <span className="text-[#5b6b7a]">Livraison IDF</span>
                <span className="font-semibold" data-testid="cart-shipping">
                  {totals.shipping === 0 ? "Offerte" : `${totals.shipping.toFixed(2).replace(".", ",")} €`}
                </span>
              </div>
              {totals.subtotal > 0 && totals.subtotal < 50 && (
                <div className="w-full text-xs text-[#5b6b7a] italic">
                  Plus que {(50 - totals.subtotal).toFixed(2).replace(".", ",")} € pour la livraison offerte
                </div>
              )}
              <div className="w-full flex justify-between border-t border-[#e6edf3] pt-3">
                <span className="font-display font-bold text-lg uppercase">Total</span>
                <span className="font-display font-extrabold text-xl" data-testid="cart-total">
                  {totals.total.toFixed(2).replace(".", ",")} €
                </span>
              </div>
              <Button
                onClick={handleCheckout}
                className="w-full h-12 bg-[#FF7A00] hover:bg-[#E66E00] text-white rounded-sm text-base font-semibold"
                data-testid="cart-checkout-button"
              >
                Finaliser la commande
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;
